import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  purchasing_requisitions_highlight_most_expensive,
  purchasing_requisitions_highlight_cheapest,
  purchasing_requisition_code,
  purchasing_pdf_show_company_notes,
  purchasing_pdf_show_invoices_should_be_sent_to,
  purchasing_pdf_show_invoices_should_be_issued_to,
  purchasing_requisition_prefill_po_company,
  PurchasingType,
  PurchasingRequisitionHighlightMostExpensive,
  PurchasingRequisitionHighlightCheapest,
  PurchasingRequisitionBooleanOption
} from 'views/companies/settings/purchasing/config';
import { formatBooleanSelectorValue } from '@/store/company-settings/helpers';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import InfoPanel from '@/common/components/general/InfoPanel';
import Input from 'common/components/form/inputs/Input';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type PurchasingProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.purchasing;
const CATEGORY_TITLE = 'Purchasing';

const TRUE_FALSE_OPTIONS = [
  { label: 'False', value: '0' },
  { label: 'True', value: '1' }
];

const YES_NO_OPTIONS = [
  { label: 'No', value: '0' },
  { label: 'Yes', value: '1' }
];

const ENABLED_OPTIONS = [
  { label: 'Disabled', value: '0' },
  { label: 'Enabled', value: '1' }
];

const LABELS = {
  [purchasing_requisitions_highlight_most_expensive]: 'Highlight most expensive requisitions',
  [purchasing_requisitions_highlight_cheapest]: 'Highlight cheapest requisitions',
  [purchasing_requisition_code]: 'Purchasing code organization',
  [purchasing_pdf_show_company_notes]: 'PO Export - Show the company notes section',
  [purchasing_pdf_show_invoices_should_be_sent_to]:
    'PO Export - Show "Invoices should be sent to" section',
  [purchasing_pdf_show_invoices_should_be_issued_to]:
    'PO Export - Show "Invoices should be issued to" section',
  [purchasing_requisition_prefill_po_company]: 'Prefill "Invoice to a company" in PO details'
};

const Purchasing: FC<PurchasingProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, changeField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<PurchasingType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [purchasing_requisitions_highlight_most_expensive]:
        companySettings.purchasing_requisitions_highlight_most_expensive,
      [purchasing_requisitions_highlight_cheapest]:
        companySettings.purchasing_requisitions_highlight_cheapest,
      [purchasing_requisition_code]: companySettings.purchasing_requisition_code,
      [purchasing_pdf_show_company_notes]: companySettings.purchasing_pdf_show_company_notes,
      [purchasing_pdf_show_invoices_should_be_sent_to]:
        companySettings.purchasing_pdf_show_invoices_should_be_sent_to,
      [purchasing_pdf_show_invoices_should_be_issued_to]:
        companySettings.purchasing_pdf_show_invoices_should_be_issued_to,
      [purchasing_requisition_prefill_po_company]:
        companySettings.purchasing_requisition_prefill_po_company
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as PurchasingType | null,
    toPayload: values => [
      {
        key: purchasing_requisitions_highlight_most_expensive,
        value: formatBooleanSelectorValue(values[purchasing_requisitions_highlight_most_expensive])
      },
      {
        key: purchasing_requisitions_highlight_cheapest,
        value: formatBooleanSelectorValue(values[purchasing_requisitions_highlight_cheapest])
      },
      { key: purchasing_requisition_code, value: values[purchasing_requisition_code] },
      {
        key: purchasing_pdf_show_company_notes,
        value: formatBooleanSelectorValue(values[purchasing_pdf_show_company_notes])
      },
      {
        key: purchasing_pdf_show_invoices_should_be_sent_to,
        value: formatBooleanSelectorValue(values[purchasing_pdf_show_invoices_should_be_sent_to])
      },
      {
        key: purchasing_pdf_show_invoices_should_be_issued_to,
        value: formatBooleanSelectorValue(values[purchasing_pdf_show_invoices_should_be_issued_to])
      },
      {
        key: purchasing_requisition_prefill_po_company,
        value: formatBooleanSelectorValue(values[purchasing_requisition_prefill_po_company])
      }
    ],
    labels: LABELS,
    formatValue: (key, value) => {
      if (
        key === purchasing_requisitions_highlight_most_expensive ||
        key === purchasing_requisitions_highlight_cheapest
      ) {
        return formatOptionDisplay(value, TRUE_FALSE_OPTIONS);
      }
      if (key === purchasing_requisition_prefill_po_company) {
        return formatOptionDisplay(value, ENABLED_OPTIONS);
      }
      if (
        key === purchasing_pdf_show_company_notes ||
        key === purchasing_pdf_show_invoices_should_be_sent_to ||
        key === purchasing_pdf_show_invoices_should_be_issued_to
      ) {
        return formatOptionDisplay(value, YES_NO_OPTIONS);
      }
      return formatOptionDisplay(value);
    },
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Requisitions"
        hidden={
          !isSettingVisible(CATEGORY_ID, purchasing_requisitions_highlight_most_expensive) &&
          !isSettingVisible(CATEGORY_ID, purchasing_requisitions_highlight_cheapest)
        }
        className="border-bottom border-gray-200"
      >
        <VisibleSetting
          categoryId={CATEGORY_ID}
          settingKey={purchasing_requisitions_highlight_most_expensive}
        >
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title="PURCHASING REQUISITIONS"
                apiKey={purchasing_requisitions_highlight_most_expensive}
                className="cmb-4"
              />
              <Select
                options={TRUE_FALSE_OPTIONS}
                onChange={(option: PurchasingRequisitionHighlightMostExpensive) => {
                  selectField(purchasing_requisitions_highlight_most_expensive)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_requisitions_highlight_most_expensive]}
              />
            </Col>
          </Row>
        </VisibleSetting>
        <VisibleSetting
          categoryId={CATEGORY_ID}
          settingKey={purchasing_requisitions_highlight_cheapest}
        >
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title="PURCHASING REQUISITIONS"
                apiKey={purchasing_requisitions_highlight_cheapest}
                className="cmb-4"
              />
              <Select
                options={TRUE_FALSE_OPTIONS}
                onChange={(option: PurchasingRequisitionHighlightCheapest) => {
                  selectField(purchasing_requisitions_highlight_cheapest)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_requisitions_highlight_cheapest]}
              />
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>

      <SubCategory
        title="PO"
        hidden={
          !isSettingVisible(CATEGORY_ID, purchasing_requisition_prefill_po_company) &&
          !isSettingVisible(CATEGORY_ID, purchasing_pdf_show_invoices_should_be_issued_to) &&
          !isSettingVisible(CATEGORY_ID, purchasing_pdf_show_company_notes) &&
          !isSettingVisible(CATEGORY_ID, purchasing_pdf_show_invoices_should_be_sent_to)
        }
        className="border-bottom border-gray-200"
      >
        <VisibleSetting
          categoryId={CATEGORY_ID}
          settingKey={purchasing_requisition_prefill_po_company}
        >
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title='AUTO-FILL "INVOICE TO A COMPANY" IN PO DETAILS'
                apiKey={purchasing_requisition_prefill_po_company}
                className="cmb-4"
              />
              <Select
                options={ENABLED_OPTIONS}
                onChange={(option: PurchasingRequisitionBooleanOption) => {
                  selectField(purchasing_requisition_prefill_po_company)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_requisition_prefill_po_company]}
              />
            </Col>
          </Row>
        </VisibleSetting>
        <VisibleSetting
          categoryId={CATEGORY_ID}
          settingKey={purchasing_pdf_show_invoices_should_be_issued_to}
        >
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title='PO EXPORT - SHOW "INVOICES SHOULD BE ISSUED TO" SECTION'
                apiKey={purchasing_pdf_show_invoices_should_be_issued_to}
                className="cmb-4"
              />
              <Select
                options={YES_NO_OPTIONS}
                onChange={(option: PurchasingRequisitionBooleanOption) => {
                  selectField(purchasing_pdf_show_invoices_should_be_issued_to)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_pdf_show_invoices_should_be_issued_to]}
              />
            </Col>
          </Row>
        </VisibleSetting>
        <VisibleSetting categoryId={CATEGORY_ID} settingKey={purchasing_pdf_show_company_notes}>
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title="PO EXPORT - SHOW THE COMPANY NOTES SECTION"
                apiKey={purchasing_pdf_show_company_notes}
                className="cmb-4"
              />
              <Select
                options={YES_NO_OPTIONS}
                onChange={(option: PurchasingRequisitionBooleanOption) => {
                  selectField(purchasing_pdf_show_company_notes)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_pdf_show_company_notes]}
              />
            </Col>
          </Row>
        </VisibleSetting>
        <VisibleSetting
          categoryId={CATEGORY_ID}
          settingKey={purchasing_pdf_show_invoices_should_be_sent_to}
        >
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title='PO EXPORT - SHOW "INVOICES SHOULD BE SENT TO" SECTION'
                apiKey={purchasing_pdf_show_invoices_should_be_sent_to}
                className="cmb-4"
              />
              <Select
                options={YES_NO_OPTIONS}
                onChange={(option: PurchasingRequisitionBooleanOption) => {
                  selectField(purchasing_pdf_show_invoices_should_be_sent_to)(option);
                }}
                className="min-width-150 max-width-150 pe-2"
                {...fields[purchasing_pdf_show_invoices_should_be_sent_to]}
              />
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>

      <SubCategory
        title="Requisition code structure"
        hidden={!isSettingVisible(CATEGORY_ID, purchasing_requisition_code)}
        className="cpb-2 cmb-2"
      >
        <VisibleSetting categoryId={CATEGORY_ID} settingKey={purchasing_requisition_code}>
          <Row className="d-flex align-items-center gx-1">
            <Col>
              <InputLabel
                title="PURCHASING CODE ORGANIZATION"
                apiKey={purchasing_requisition_code}
                className="cmb-6"
              />
              <InfoPanel className="cmb-6 max-width-580">
                Use the following structure when creating a purchasing requisition code:
                [vessel_or_company_code][-][submission_date_yymmdd][-][origin][-][ai_per_day]
                <br />
                <br />
                You may:
                <br />• Change the placement/order of the elements (brackets).
                <br />• You can move, replace, or add dashes - as needed
              </InfoPanel>
              <Input
                type="text"
                onChange={changeField(purchasing_requisition_code)}
                className="organization-code-input"
                inputClassName="min-width-580 max-width-580"
                {...fields[purchasing_requisition_code]}
              />
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>
    </Category>
  );
};

export default Purchasing;
